
import React, { useState, useEffect, useRef } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Heart, Calendar, ChefHat, ShoppingCart, Lightbulb, X, Flame, Utensils, CalendarDays, Search, Eye, Filter } from 'lucide-react';
import { debounce } from 'lodash';


interface TooltipGuideProps {
  isActive: boolean;
  onClose: () => void;
}

interface GuideStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  targetSelector: string;
  color: string;
}

interface Step {
  [key: string]: GuideStep[]
}

const TooltipGuide: React.FC<TooltipGuideProps> = ({ isActive, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: '20%', left: '15%' });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [onboardingKey, setOnboardingKey] = useState("cookbook");

  useEffect(() => {
    let path = window.location.pathname.substring(1);
    console.log('[Navigation] URL changed:', path);
    if (path.length < 1) {
      path = 'dashboard'
    }
    setOnboardingKey(path);
  });


  const steps: Step = {
    dashboard: [
      {
        id: 'weekly-calories',
        title: 'Weekly Calories',
        description: 'Track your total calorie intake on a weekly basis',
        icon: Flame,
        targetSelector: '[data-tooltip="weekly-calories"]',
        color: 'bg-red-500'
      },
      {
        id: 'next-meal',
        title: 'Next Meal',
        description: 'Get a quick preview of your upcoming meal plan',
        icon: Utensils,
        targetSelector: '[data-tooltip="next-meal"]',
        color: 'bg-green-500'
      },
      {
        id: 'upcoming-meals',
        title: 'Upcoming Meals',
        description: 'See all your scheduled meals for the rest of the week',
        icon: CalendarDays,
        targetSelector: '[data-tooltip="upcoming-meals"]',
        color: 'bg-yellow-500'
      },
      {
        id: 'recipes',
        title: 'Explore Recipes',
        description: 'Browse hundreds of recipes filtered by your preferences',
        icon: BookOpen,
        targetSelector: '[data-tooltip="cookbook"]',
        color: 'bg-blue-500'
      },
      {
        id: 'create-plan',
        title: 'Create Plan',
        description: 'Create a meal template along with a 4-week meal plan from your template',
        icon: ChefHat,
        targetSelector: '[data-tooltip="create-plan"]',
        color: 'bg-brand-success'
      },
    ],
    "meal-planner": [
      {
        id: 'template',
        title: 'Create Template',
        description: 'Design your ideal weekly meal structure',
        icon: Calendar,
        targetSelector: '[data-tooltip="template"]',
        color: 'bg-brand-accent'
      },
      {
        id: 'generate',
        title: 'Generate Plan',
        description: 'Create a 4-week meal plan from your template',
        icon: ChefHat,
        targetSelector: '[data-tooltip="generate"]',
        color: 'bg-brand-success'
      },
      {
        id: 'shopping',
        title: 'Shopping List',
        description: 'Get organized grocery lists for your entire plan',
        icon: ShoppingCart,
        targetSelector: '[data-tooltip="shopping"]',
        color: 'bg-orange-500'
      }],
    cookbook: [
      {
        id: 'search-recipe',
        title: 'Search Recipe',
        description: 'Quickly find recipes by ingredients, meal type, or preferences',
        icon: Search,
        targetSelector: '[data-tooltip="search-recipe"]',
        color: 'bg-indigo-500'
      },
      {
        id: 'filter-recipe',
        title: 'Filter Recipe',
        description: 'Narrow down recipes based on dietary needs, time, and ingredients',
        icon: Filter,
        targetSelector: '[data-tooltip="filter-recipe"]',
        color: 'bg-teal-500'
      },
      {
        id: 'view-recipe',
        title: 'View Recipe',
        description: 'Open detailed instructions and nutrition facts for any recipe',
        icon: Eye,
        targetSelector: '[data-tooltip="view-recipe"]',
        color: 'bg-purple-500'
      },
      {
        id: 'favorites',
        title: 'Save Favourites',
        description: 'Heart recipes you love for quick access later',
        icon: Heart,
        targetSelector: '[data-tooltip="favorites"]',
        color: 'bg-red-500'
      },
    ]
  };

  const getElementPosition = (
    selector: string,
    tooltipElement: HTMLDivElement | null
  ) => {
    const element = document.querySelector(selector);
    if (!element || !tooltipElement) {
      return { top: '20%', left: '15%' };
    }

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    const tooltipRect = tooltipElement.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    const margin = 10;

    let top = rect.bottom + scrollTop + margin;
    let left = rect.left + scrollLeft + rect.width / 1.5;
    // let left = rect.left + scrollLeft + rect.width / 2 - tooltipWidth / 2;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Ensure tooltip stays within horizontal bounds
    if (left + tooltipWidth > scrollLeft + viewportWidth) {
      left = scrollLeft + viewportWidth - tooltipWidth - margin;
    }
    if (left < scrollLeft + margin) {
      left = scrollLeft + margin;
    }

    // Ensure tooltip stays within vertical bounds
    if (top + tooltipHeight > scrollTop + viewportHeight) {
      top = rect.top + scrollTop - tooltipHeight - margin;
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
    };
  };



  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      setCurrentStep(0);
    } else {
      setIsVisible(false);
    }
  }, [isActive]);

  useEffect(() => {
    const targetSelector = steps[onboardingKey]?.[currentStep]?.targetSelector;
    if (!targetSelector) {
      console.log("Target not found")
      return;
    }

    const element = document.querySelector(targetSelector);
    if (!element) {
      console.log("Element not found", element, targetSelector)
      return;
    }

    // const updatePosition = () => {
    //   const position = getElementPosition(targetSelector, null);
    //   console.log("Updating position:", position);
    //   setTooltipPosition(position);
    // };

    const updatePosition = () => {
      const position = getElementPosition(
        steps[onboardingKey]?.[currentStep]?.targetSelector,
        tooltipRef.current
      );
      console.log("Updating position:", position);
      setTooltipPosition(position);
    };

    // Debounced version for scroll/resize
    const debouncedUpdate = debounce(updatePosition, 100);

    // Observe scroll and resize
    window.addEventListener('scroll', debouncedUpdate);
    window.addEventListener('resize', debouncedUpdate);

    // Use ResizeObserver to monitor size/position changes
    const resizeObserver = new ResizeObserver(() => {
      console.log("ResizeObserver triggered");
      updatePosition();
    });

    resizeObserver.observe(element);

    // Initial update
    updatePosition();

    return () => {
      window.removeEventListener('scroll', debouncedUpdate);
      window.removeEventListener('resize', debouncedUpdate);
      resizeObserver.disconnect();
    };
  }, [currentStep]);


  const handleNext = () => {
    if (currentStep < steps[onboardingKey]?.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isVisible) return null;

  const currentStepData = steps[onboardingKey]?.[currentStep] ?? undefined;

  if (!currentStepData) return null;

  return (
    <TooltipProvider>
      <div className="fixed inset-0 z-50 pointer-events-none">
        {/* Overlay */}
        <div className="absolute inset-0 bg-brand-black/10 backdrop-blur-sm pointer-events-auto" onClick={onClose} />

        {/* Spotlight for current step */}
        <div
          className="absolute w-32 h-32 rounded-full pointer-events-none pulse-glow"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 0 0 2000px rgba(0, 0, 0, 0.7), inset 0 0 30px rgba(0, 220, 255, 0.3)',
            border: '3px solid hsl(var(--brand-cyan))',
          }}
        />

        {/* Tooltip */}
        <div
          className="absolute pointer-events-auto fade-in-up"
          ref={tooltipRef}
          style={{
            top: `calc(${tooltipPosition.top} + 80px)`,
            left: tooltipPosition.left,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="bg-brand-dark border border-brand-cyan/30 rounded-lg p-4 max-w-xs shadow-2xl">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${currentStepData.color}/20`}>
                <currentStepData.icon className={`w-5 h-5 ${currentStepData.color.replace('bg-', 'text-')}`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-brand-white text-sm mb-1">
                  {currentStepData.title}
                </h3>
                <p className="text-gray-300 text-xs leading-relaxed">
                  {currentStepData.description}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-brand-cyan/20">
              <div className="flex gap-1">
                {steps[onboardingKey]?.map((_, index) => (
                  <div
                    key={index}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${index === currentStep
                      ? 'bg-brand-cyan'
                      : index < currentStep
                        ? 'bg-brand-cyan/60'
                        : 'bg-gray-600'
                      }`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs bg-brand-cyan/20 text-brand-cyan border-0">
                  {currentStep + 1}/{steps[onboardingKey]?.length}
                </Badge>
                <div className="flex gap-1">
                  {currentStep > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handlePrevious}
                      className="h-6 px-2 text-xs text-gray-400 hover:text-brand-white"
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleNext}
                    className="h-6 px-3 text-xs bg-brand-cyan hover:bg-brand-cyan/90 text-brand-black"
                  >
                    {currentStep === steps[onboardingKey]?.length - 1 ? 'Done' : 'Next'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Close button */}
        {/* <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="absolute top-4 right-4 pointer-events-auto text-gray-400 hover:text-brand-white bg-brand-dark/50 hover:bg-brand-dark"
        >
          <X className="w-4 h-4" />
        </Button> */}

        {/* Help indicator */}
        <div className="absolute bottom-4 left-4 pointer-events-auto">
          <div className="flex items-center gap-2 bg-brand-dark/80 rounded-lg p-3 border border-brand-cyan/20">
            <Lightbulb className="w-4 h-4 text-brand-cyan" />
            <span className="text-xs text-gray-300">Interactive tour • Click anywhere to skip</span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default TooltipGuide;
